import { useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../Redux/hooks";
import {
  startTimer,
  pauseTimer,
  resumeTimer,
  updateElapsedTime,
  resetTimer,
} from "../Redux/reducers/billingTimerSlice";
import { getSocket } from "../utils/socket";
import { DateTime } from "luxon";

export const useBillingTimer = () => {
  const dispatch = useAppDispatch();
  const { participantId } = useParams<{ participantId: string }>();
  const { isActive, isPaused, elapsedTime } = useAppSelector(
    (state) => state.billingTimer
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null); // Timestamp when timer started/resumed
  const pausedElapsedTimeRef = useRef<number>(0); // Elapsed time when paused
  const isActiveRef = useRef<boolean>(isActive);
  const sessionIdRef = useRef<string | null>(null); // Session ID from start event

  // Start timer
  const handleStart = useCallback(() => {
    if (!participantId) return;

    const tryStart = () => {
      const socket = getSocket();
      if (!socket) {
        console.error("Socket not initialized");
        // Retry after a short delay if socket is not initialized
        // setTimeout(tryStart, 500);
        return;
      }

      if (!socket.connected) {
        console.warn("Socket not connected, waiting for connection...");
        // Wait for socket connection
        const onConnect = () => {
          socket.off("connect", onConnect);
          socket.emit(
            "participant_view_start",
            { participantId, timestamp: DateTime.now().toUTC().toISO() },
            (response: any) => {
              const initialTime = response.activeDuration ?? 0;
              sessionIdRef.current = response.sessionId ?? null;
              pausedElapsedTimeRef.current = initialTime;
              startTimeRef.current = Date.now();
              dispatch(
                startTimer({
                  participantId,
                  initialTime: initialTime,
                })
              );
            }
          );
        };
        socket.on("connect", onConnect);
        return;
      }

      // elapsedTimeRef.current = 0;
      // dispatch(
      //   startTimer({
      //     participantId,
      //     initialTime: 0,
      //   })
      // );
      // lastUpdateTimeRef.current = Date.now();

      console.log("start event called with payload", {
        participantId,
        timestamp: DateTime.now().toUTC().toISO(),
      });

      socket.emit(
        "participant_view_start",
        { participantId, timestamp: DateTime.now().toUTC().toISO() },
        (response: any) => {
          console.log("response for start event", response);
          const initialTime = response.activeDuration ?? 0;
          sessionIdRef.current = response.sessionId ?? null;
          pausedElapsedTimeRef.current = initialTime;
          startTimeRef.current = Date.now();
          dispatch(
            startTimer({
              participantId,
              initialTime: initialTime,
            })
          );
        }
      );
    };

    tryStart();
  }, [participantId, dispatch]);

  // Pause timer
  const handlePause = useCallback(() => {
    if (!isActive || isPaused) return;

    const socket = getSocket();
    if (!socket || !socket.connected) {
      console.error("Socket not connected");
      return;
    }

    // Calculate current elapsed time before pausing
    if (startTimeRef.current !== null) {
      const now = Date.now();
      const elapsedSinceStart = Math.floor((now - startTimeRef.current) / 1000);
      pausedElapsedTimeRef.current =
        pausedElapsedTimeRef.current + elapsedSinceStart;
      startTimeRef.current = null;
    }

    dispatch(pauseTimer());

    console.log("pause event called with payload", {
      participantId,
      sessionId: sessionIdRef.current,
      timestamp: DateTime.now().toUTC().toISO(),
    });

    socket.emit(
      "participant_view_pause",
      {
        participantId,
        sessionId: sessionIdRef.current,
        timestamp: DateTime.now().toUTC().toISO(),
      },
      (response: any) => {
        console.log("response for pause event", response);
      }
    );
  }, [isActive, isPaused, participantId, dispatch]);

  // Resume timer
  const handleResume = useCallback(() => {
    if (!isActive || !isPaused) return;

    const socket = getSocket();
    if (!socket || !socket.connected) {
      console.error("Socket not connected");
      return;
    }

    console.log("resume event called with payload", {
      participantId,
      sessionId: sessionIdRef.current,
      timestamp: DateTime.now().toUTC().toISO(),
    });

    socket.emit(
      "participant_view_resume",
      {
        participantId,
        sessionId: sessionIdRef.current,
        timestamp: DateTime.now().toUTC().toISO(),
      },
      (response: any) => {
        console.log("response for resume event", response);
        if (response.success) {
          const resumeTime =
            response.activeDuration ?? pausedElapsedTimeRef.current;
          pausedElapsedTimeRef.current = resumeTime;
          startTimeRef.current = Date.now();
          dispatch(resumeTimer(resumeTime));
        } else {
          handleStart();
        }
      }
    );
  }, [isActive, isPaused, participantId, dispatch, handleStart]);

  // Stop timer
  const handleStop = useCallback(() => {
    // Use ref to get current isActive value to avoid closure issues
    if (!isActiveRef.current) return;

    const socket = getSocket();
    if (socket && socket.connected && participantId) {
      console.log("end event called");
      socket.emit(
        "participant_view_end",
        {
          participantId: participantId,
          sessionId: sessionIdRef.current,
          timestamp: DateTime.now().toUTC().toISO(),
        },
        () => {
          dispatch(resetTimer());
        }
      );
      dispatch(resetTimer());
    } else {
      dispatch(resetTimer());
    }

    // Clear refs
    startTimeRef.current = null;
    pausedElapsedTimeRef.current = 0;
    sessionIdRef.current = null;
  }, [participantId, dispatch]);

  // Sync refs with Redux state
  useEffect(() => {
    isActiveRef.current = isActive;
    // Update paused elapsed time when state changes (e.g., from server sync)
    if (isPaused || !isActive) {
      pausedElapsedTimeRef.current = elapsedTime;
      if (!isActive) {
        // Timer was reset or stopped
        startTimeRef.current = null;
        pausedElapsedTimeRef.current = 0;
        sessionIdRef.current = null;
      }
    }
  }, [elapsedTime, isActive, isPaused]);

  // Update timer - calculate based on actual elapsed time to prevent drift
  useEffect(() => {
    if (isActive && !isPaused && startTimeRef.current !== null) {
      // Use shorter interval (100ms) for smoother updates, but calculate based on actual time
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        // Calculate elapsed time based on start time, not by incrementing
        const elapsedSinceStart = Math.floor(
          (now - startTimeRef.current!) / 1000
        );
        const totalElapsedTime =
          pausedElapsedTimeRef.current + elapsedSinceStart;

        // Only update Redux if the second value has changed (to avoid unnecessary re-renders)
        const currentSecond = Math.floor(totalElapsedTime);
        const previousSecond = Math.floor(elapsedTime);

        if (currentSecond !== previousSecond) {
          dispatch(updateElapsedTime(totalElapsedTime));
        }
      }, 500); // Update every 100ms for smoothness, but Redux only updates on second change
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isActive, isPaused, elapsedTime, dispatch]);

  // Handle visibility change (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden
        if (isActive && !isPaused) {
          handlePause();
        }
      } else {
        // Tab is visible
        if (isActive && isPaused) {
          handleResume();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isActive, isPaused, handleResume, handlePause]);

  return {
    handleStart,
    handlePause,
    handleResume,
    handleStop,
  };
};
